import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PaginationMeta } from '@/types/api';
import { cn } from '@/lib/utils';
import { RowsPerPageSelect } from './RowsPerPageSelect';

export interface PaginationProps {
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  hideBorder?: boolean;
  /**
   * What the rows are, for the range line — "of 18 classes" rather than
   * "of 18". Worth passing wherever the table's own heading is out of view by
   * the time the reader reaches the bottom of a long list.
   */
  itemLabel?: string;
  /**
   * Draws the numbered strip between the arrows, for a list somebody jumps
   * around in. A long feed people only page forward through is better without
   * it — twelve numbers is twelve targets nobody aims at.
   */
  showPageNumbers?: boolean;
  /** Offers a rows-per-page control. Both of these are needed, or neither. */
  limitOptions?: readonly number[];
  onLimitChange?: (limit: number) => void;
  summaryTextClass?: string;
}

/** How many numbered buttons the strip draws before it starts eliding. */
const WINDOW = 7;

/**
 * The pages worth drawing: always the first and the last, always the current one
 * and its neighbours, and a gap standing in for everything skipped.
 */
function pageWindow(page: number, totalPages: number): (number | 'gap')[] {
  if (totalPages <= WINDOW) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const wanted = new Set([1, totalPages, page, page - 1, page + 1]);
  // Keep the strip a fixed width wherever the reader is: at either end the
  // window has one neighbour instead of two, so it borrows from the other side.
  if (page <= 3) [2, 3, 4].forEach((value) => wanted.add(value));
  if (page >= totalPages - 2) {
    [totalPages - 1, totalPages - 2, totalPages - 3].forEach((value) => wanted.add(value));
  }

  const pages = [...wanted].filter((value) => value >= 1 && value <= totalPages).sort((a, b) => a - b);

  return pages.flatMap((value, index) => {
    const previous = pages[index - 1];
    return previous !== undefined && value - previous > 1 ? ['gap' as const, value] : [value];
  });
}

/**
 * The arrows, in the palette of whichever console they are drawn in.
 *
 * Tokens rather than a fixed slate, because these controls appear on every list
 * in the product and each area re-declares `--accent`, `--line` and `--surface`
 * on its own root (CLAUDE.md §7). A hard-coded shade made the same strip read
 * as a different product's from one screen to the next — and left the selected
 * page green inside a blue console.
 */
const STEP =
  'inline-flex h-7 shrink-0 items-center justify-center rounded-field border border-line bg-surface text-ink-2 text-xs font-bold transition-colors hover:bg-surface-2 hover:text-ink disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line';

/**
 * Page controls for every list in the product.
 *
 * The row range leads, because "showing 1–20 of 342" answers the question people
 * actually have. Numbers and a rows-per-page control are opt-in: a phone-width
 * list is better with two arrows than with a strip that wraps onto its own line.
 */
export function Pagination({
  meta,
  onPageChange,
  hideBorder = false,
  itemLabel,
  showPageNumbers = false,
  limitOptions,
  onLimitChange,
  summaryTextClass,
}: PaginationProps) {
  const { page, limit, total, totalPages } = meta;
  if (total === 0) return null;

  const firstRow = (page - 1) * limit + 1;
  const lastRow = Math.min(page * limit, total);
  // Both halves or neither — a rows-per-page box with nowhere to report to would
  // look like a working control and silently do nothing.
  const limitControl =
    limitOptions && limitOptions.length > 0 && onLimitChange
      ? { options: limitOptions, onChange: onLimitChange }
      : null;

  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 sm:gap-x-4',
        !hideBorder && 'border-t border-line',
      )}
    >
      <p className={cn('text-xs font-medium text-ink-2 w-full sm:w-auto text-center sm:text-left', summaryTextClass)}>
        Showing <span className="font-bold text-ink">{firstRow}</span> to{' '}
        <span className="font-bold text-ink">{lastRow}</span> of{' '}
        <span className="font-bold text-ink">{total}</span>
        {itemLabel ? ` ${itemLabel}` : ''}
      </p>

      <div className="flex flex-wrap items-center justify-center gap-1.5 w-full sm:w-auto">
        <button
          type="button"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className={cn(STEP, 'px-2.5 gap-1')}
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          <span>Prev</span>
        </button>

        {showPageNumbers ? (
          <ol className="flex items-center gap-1">
            {pageWindow(page, totalPages).map((entry, index) =>
              entry === 'gap' ? (
                <li
                  key={`gap-${index}`}
                  aria-hidden
                  className="grid h-7 w-5 place-items-center text-xs font-bold text-ink-3"
                >
                  …
                </li>
              ) : (
                <li key={entry}>
                  <button
                    type="button"
                    aria-label={`Page ${entry}`}
                    aria-current={entry === page ? 'page' : undefined}
                    onClick={() => onPageChange(entry)}
                    className={cn(
                      'inline-flex h-7 min-w-7 items-center justify-center rounded-field border px-2 text-xs font-bold tabular-nums transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line',
                      // The page you are on is the accent of whichever console
                      // this list belongs to, so it matches the tabs and the
                      // buttons above it rather than announcing a colour of its
                      // own.
                      entry === page
                        ? 'border-accent bg-accent font-extrabold text-white'
                        : 'border-line bg-surface text-ink-2 hover:border-accent hover:text-accent',
                    )}
                  >
                    {entry}
                  </button>
                </li>
              ),
            )}
          </ol>
        ) : (
          <span className="rounded-pill border border-line bg-surface-2 px-2.5 py-0.5 font-mono text-xs font-bold tabular-nums text-ink-2">
            {page} / {totalPages}
          </span>
        )}

        <button
          type="button"
          aria-label="Next page"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className={cn(STEP, 'px-2.5 gap-1')}
        >
          <span>Next</span>
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      {limitControl && (
        <RowsPerPageSelect
          className="w-full justify-center sm:w-auto sm:justify-end"
          value={limit}
          options={limitControl.options}
          onChange={limitControl.onChange}
        />
      )}
    </div>
  );
}
